import React, { useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { PaymentInstallmentBankTransferEntity } from '../../../../../../api/types/students/user-details-student';
import { UserSummaryType } from '../../../../../../api/types/user/user-summary.type';
import { reduceInstallmentPaymentBankTransferOperation } from '../../operations/reduce-installment-payment-bank-transfer-operation';

interface ReduceInstallmentPaymentBankTransferDialogProps {
  visible: boolean;
  user: UserSummaryType;
  installmentPlanId: number;
  installment: PaymentInstallmentBankTransferEntity | null;
  onHide: () => void;
}

/** After reduction, the remaining installment amount must stay at least this value (₪). */
const MIN_REMAINING_PAYMENT = 5;

export const ReduceInstallmentPaymentBankTransferDialog: React.FC<
  ReduceInstallmentPaymentBankTransferDialogProps
> = ({ visible, user, installmentPlanId, installment, onHide }) => {
  const [reduceAmount, setReduceAmount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const installmentAmount = installment?.amount ?? null;
  /** Max reduction so that (amount - reduce) >= MIN_REMAINING_PAYMENT */
  const maxReduceAllowed = useMemo(() => {
    if (installmentAmount === null || installmentAmount === undefined)
      return null;
    return Math.max(0, installmentAmount - MIN_REMAINING_PAYMENT);
  }, [installmentAmount]);

  const isValid = useMemo(() => {
    if (maxReduceAllowed === null || maxReduceAllowed <= 0) return false;
    if (reduceAmount === null) return false;
    if (!Number.isFinite(reduceAmount) || reduceAmount <= 0) return false;
    // Allow up to 2 digits after the decimal point.
    if (Math.round(reduceAmount * 100) / 100 !== reduceAmount) return false;
    return reduceAmount <= maxReduceAllowed;
  }, [reduceAmount, maxReduceAllowed]);

  const handleClose = () => {
    if (loading) return;
    setReduceAmount(null);
    onHide();
  };

  const handleApprove = async () => {
    if (!installment || reduceAmount === null || !isValid) return;

    await reduceInstallmentPaymentBankTransferOperation(
      user,
      installmentPlanId,
      installment.id,
      reduceAmount,
      setLoading,
      handleClose,
    );
  };

  return (
    <Dialog
      header="הפחתת תשלום (העברה בנקאית)"
      visible={visible}
      onHide={handleClose}
      style={{ width: '560px', direction: 'rtl' }}
      modal
      closable={!loading}
      footer={
        <div
          style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
        >
          <Button
            label="ביטול"
            icon="pi pi-times"
            severity="secondary"
            text
            onClick={handleClose}
            disabled={loading}
          />
          <Button
            label="אישור הפחתה"
            icon="pi pi-check"
            severity="warning"
            onClick={handleApprove}
            disabled={!isValid || loading}
            loading={loading}
          />
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '14px 16px',
            backgroundColor: '#EFF6FF',
            borderRadius: '10px',
            border: '1px solid #BFDBFE',
          }}
        >
          <i
            className="pi pi-info-circle"
            style={{ fontSize: '22px', color: '#2563EB', marginTop: '2px' }}
          />
          <div style={{ color: '#1E3A8A' }}>
            <div style={{ fontWeight: 700, marginBottom: '4px' }}>
              חשוב לדעת
            </div>
            <div style={{ fontSize: '14px', lineHeight: 1.5 }}>
              מכיוון שהוראת הקבע (העברה בנקאית) מקושרת לשירותי התשלום, הפחתת
              סכום התשלום תעדכן גם את ההקצאה (allocation) בהתאם בשירותי התשלום
              המקושרים.
            </div>
          </div>
        </div>

        <div
          style={{
            padding: '16px',
            backgroundColor: '#F3F4F6',
            borderRadius: '10px',
          }}
        >
          {installment ? (
            <>
              <div
                style={{
                  fontSize: '14px',
                  color: '#374151',
                  marginBottom: '12px',
                }}
              >
                תשלום מספר <b>{installment.installmentNumber}</b> בסכום נוכחי{' '}
                <b>₪{installment.amount?.toLocaleString()}</b>
              </div>
              <div
                style={{
                  fontSize: '13px',
                  color: '#4B5563',
                  lineHeight: 1.5,
                  marginBottom: '12px',
                }}
              >
                לא ניתן להשאיר תשלום נמוך מ-₪{MIN_REMAINING_PAYMENT} לאחר הפחתה
                {maxReduceAllowed !== null && maxReduceAllowed > 0 ? (
                  <>
                    {' '}
                    (בתשלום הנוכחי ניתן להפחית עד ₪
                    {maxReduceAllowed.toLocaleString()}, כך שנשאר לפחות ₪
                    {MIN_REMAINING_PAYMENT})
                  </>
                ) : null}
                . אם יש צורך להסיר את התשלום במלואו, ניתן לבטל את התשלום דרך
                פעולת הביטול.
              </div>
            </>
          ) : (
            <div style={{ fontSize: '14px', color: '#6B7280' }}>
              לא נבחר תשלום להפחתה.
            </div>
          )}

          <label
            htmlFor="reduceAmount"
            style={{
              display: 'block',
              marginBottom: '10px',
              fontWeight: 700,
              color: '#374151',
            }}
          >
            {maxReduceAllowed !== null && maxReduceAllowed > 0
              ? `סכום להפחתה (עד ₪${maxReduceAllowed.toLocaleString()} — נשאר לפחות ₪${MIN_REMAINING_PAYMENT}) *`
              : 'סכום להפחתה *'}
          </label>

          <InputNumber
            id="reduceAmount"
            value={reduceAmount}
            onValueChange={(e) => setReduceAmount(e.value)}
            min={1}
            max={
              typeof maxReduceAllowed === 'number' && maxReduceAllowed > 0
                ? maxReduceAllowed
                : undefined
            }
            minFractionDigits={0}
            maxFractionDigits={2}
            useGrouping
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', textAlign: 'right' }}
            disabled={
              loading ||
              !installment ||
              maxReduceAllowed === null ||
              maxReduceAllowed <= 0
            }
            placeholder="הזן סכום להפחתה"
          />

          {installment &&
            maxReduceAllowed !== null &&
            maxReduceAllowed <= 0 && (
              <div
                style={{
                  marginTop: '10px',
                  fontSize: '13px',
                  color: '#92400E',
                }}
              >
                לא ניתן להפחית: סכום התשלום (₪
                {installment.amount?.toLocaleString()}) אינו מאפשר להשאיר לפחות
                ₪{MIN_REMAINING_PAYMENT} לאחר הפחתה. לביטול התשלום במלואו השתמש
                בפעולת הביטול.
              </div>
            )}

          {!isValid &&
            reduceAmount !== null &&
            installment &&
            maxReduceAllowed !== null &&
            maxReduceAllowed > 0 && (
              <div
                style={{
                  marginTop: '10px',
                  fontSize: '13px',
                  color: '#B91C1C',
                }}
              >
                סכום ההפחתה חייב להיות מספר חיובי שלא יעבור את ₪
                {maxReduceAllowed.toLocaleString()} (כך שנשאר לפחות ₪
                {MIN_REMAINING_PAYMENT}).
              </div>
            )}
        </div>

        <div
          style={{
            padding: '12px',
            backgroundColor: '#FEF2F2',
            borderRadius: '10px',
            border: '1px solid #FECACA',
            fontSize: '14px',
            color: '#991B1B',
          }}
        >
          <i
            className="pi pi-exclamation-triangle"
            style={{ marginLeft: '8px' }}
          />
          הפחתה תשפיע על שירותי התשלום המקושרים להוראת הקבע.
        </div>
      </div>
    </Dialog>
  );
};
